import React, { useCallback, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import logoSistema from './assets/logosistema.png';
import DashboardLayout from './components/DashboardLayout';
import Painel from './pages/Painel';
import Pacientes from './pages/Pacientes';
import PacienteNovo from './pages/PacienteNovo';
import PacienteDetalhes from './pages/PacienteDetalhes';
import Agenda from './pages/Agenda';
import Financeiro from './pages/Financeiro';
import Estoque from './pages/Estoque';
import MedicamentoDetalhes from './pages/MedicamentoDetalhes';
import Configuracoes from './pages/Configuracoes';
import { AUTH_EXPIRED_EVENT, api, clearAccessToken, refreshSession } from './services/api';

export default function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [sessionNotice, setSessionNotice] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState('');
  const [patients, setPatients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [clinicData, setClinicData] = useState({
    clinicName: '',
    legalName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    logoFileName: ''
  });

  const navigate = useNavigate();

  const clearSession = useCallback(() => {
    clearAccessToken();
    setIsAuthenticated(false);
    setCurrentUser('');
    setUsername('');
    setPassword('');
    setPatients([]);
  }, []);

  useEffect(() => {
    let active = true;

    const bootstrapSession = async () => {
      try {
        const data = await refreshSession();
        if (!active) return;
        setIsAuthenticated(!!data?.accessToken);
        setCurrentUser(data?.username || '');
      } catch (err) {
        if (!active) return;
        clearSession();
      } finally {
        if (active) {
          setAuthChecked(true);
        }
      }
    };

    bootstrapSession();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleExpiredSession = () => {
      clearSession();
      setError('');
      setSessionNotice('Sua sessão expirou. Entre novamente para continuar.');
      navigate('/', { replace: true });
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpiredSession);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpiredSession);
  }, [clearSession, navigate]);

  // Carregar pacientes quando autenticado
  useEffect(() => {
    if (isAuthenticated) {
      loadPatients();
      loadSettings();
    }
  }, [isAuthenticated]);

  // Aplicar tema quando settings mudar
  useEffect(() => {
    if (settings?.theme) {
      const primaryColor = settings.theme.primaryColor || '#511c60';
      const buttonColor = settings.theme.buttonColor || '#511c60';
      
      // Converter hex para RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 81, g: 28, b: 96 };
      };
      
      const primaryRgb = hexToRgb(primaryColor);
      const buttonRgb = hexToRgb(buttonColor);
      
      // Aplicar cores base
      document.documentElement.style.setProperty('--primary-color', primaryColor);
      document.documentElement.style.setProperty('--button-color', buttonColor);
      document.documentElement.style.setProperty('--icon-color', primaryColor);
      
      // Aplicar variações RGB para transparências
      document.documentElement.style.setProperty('--primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      document.documentElement.style.setProperty('--button-rgb', `${buttonRgb.r}, ${buttonRgb.g}, ${buttonRgb.b}`);
    }
  }, [settings]);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    }
  };

  const loadPatients = async () => {
    try {
      const data = await api.getPatients();
      setPatients(data);
    } catch (err) {
      console.error('Erro ao carregar pacientes:', err);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSessionNotice('');
    setLoading(true);

    try {
      const data = await api.login(username, password);

      setIsAuthenticated(true);
      setCurrentUser(data?.username || username);
      setUsername('');
      setPassword('');
      navigate('/painel', { replace: true });
    } catch (err) {
      setError(err.message || 'Erro inesperado');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'logo') {
      const file = files && files[0];
      setClinicData((prev) => ({ ...prev, logoFileName: file ? file.name : '' }));
      return;
    }
    setClinicData((prev) => ({ ...prev, [name]: value }));
  };

  const ProtectedRoute = ({ children }) => {
    if (!authChecked) return null;
    return isAuthenticated ? children : <Navigate to="/" replace />;
  };

  const handleAddPatient = async (patientData) => {
    try {
      const newPatient = await api.createPatient(patientData);
      setPatients((prev) => [...prev, newPatient]);
      return newPatient;
    } catch (err) {
      console.error('Erro ao criar paciente:', err);
      throw err;
    }
  };

  const handleUpdatePatient = async (updatedPatient) => {
    if (!updatedPatient?._id) return;
    try {
      const updated = await api.updatePatient(updatedPatient._id, updatedPatient);
      setPatients((prev) => prev.map((patient) => (patient._id === updated._id ? updated : patient)));
    } catch (err) {
      console.error('Erro ao atualizar paciente:', err);
    }
  };

  const handleRefreshPatient = async (patientId) => {
    if (!patientId) return;
    try {
      const updated = await api.getPatient(patientId);
      setPatients((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
    } catch (err) {
      console.error('Erro ao atualizar paciente no contexto:', err);
    }
  };

  const loginView = (
    <div className="app-shell">
      <form className="mac-card login-form" onSubmit={handleSubmit}>
        <img src={logoSistema} alt="Logo do sistema" className="logo" />
        <div>
          <h2>Iniciar sessão</h2>
          <p className="helper-text">Digite seu usuário e senha para continuar</p>
        </div>
        {sessionNotice && <span className="session-notice">{sessionNotice}</span>}
        <input
          className="mac-input"
          type="text"
          placeholder="Usuário"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <input
          className="mac-input"
          type="password"
          placeholder="Senha"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <span className="error-text">{error}</span>}
        <button className="mac-button" type="submit" disabled={loading}>
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </form>
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={authChecked && isAuthenticated ? <Navigate to="/painel" replace /> : loginView} />
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout
              currentUser={currentUser}
              clinicData={clinicData}
              onClinicChange={handleConfigChange}
              patients={patients}
              onAddPatient={handleAddPatient}
              onUpdatePatient={handleUpdatePatient}
              onRefreshPatient={handleRefreshPatient}
              settings={settings}
              onSettingsUpdate={loadSettings}
              onLogout={async () => {
                await api.logout();
                clearSession();
                navigate('/', { replace: true });
              }}
            />
          </ProtectedRoute>
        }
      >
        <Route path="/painel" element={<Painel />} />
        <Route path="/pacientes" element={<Pacientes />} />
        <Route path="/pacientes/novo" element={<PacienteNovo />} />
        <Route path="/pacientes/detalhes" element={<PacienteDetalhes />} />
        <Route path="/agenda" element={<Agenda />} />
        <Route path="/financeiro" element={<Financeiro />} />
        <Route path="/estoque" element={<Estoque />} />
        <Route path="/estoque/medicamentos/:id" element={<MedicamentoDetalhes />} />
        <Route path="/configuracoes" element={<Configuracoes />} />
      </Route>
      <Route path="*" element={<Navigate to={isAuthenticated ? '/painel' : '/'} replace />} />
    </Routes>
  );
}
